import { useMemo, useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import Modal from '../Modal';

function groupPrestationsByCategory(prestations, searchTerm) {
  const term = searchTerm.trim().toLowerCase();
  const filtered = prestations.filter((p) => {
    if (!term) return true;
    return (
      p.nom?.toLowerCase().includes(term) ||
      p.categorie?.toLowerCase().includes(term)
    );
  });
  const groups = {};
  for (const p of filtered) {
    const key = p.categorie?.trim() || 'Autres';
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
  }
  return { filtered, groups };
}

export default function PrestationsBulkModal({
  isOpen,
  onClose,
  prestations,
  initialSelectedIds,
  onSave,
  saving,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(() => new Set(initialSelectedIds || []));

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set(initialSelectedIds || []));
      setSearchTerm('');
    }
  }, [isOpen, initialSelectedIds]);

  const { filtered, groups } = useMemo(
    () => groupPrestationsByCategory(prestations, searchTerm),
    [prestations, searchTerm],
  );

  const selectedCount = selected.size;
  const totalCount = prestations.length;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCategory = (items, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const p of items) {
        if (checked) next.add(p.id);
        else next.delete(p.id);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(prestations.map((p) => p.id)));
  };

  const deselectAll = () => setSelected(new Set());

  const handleSave = () => {
    onSave([...selected]);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Gérer les prestations"
      maxWidth="max-w-3xl"
      className="sm:max-h-[90vh]"
    >
      <div className="flex max-h-[min(85vh,40rem)] flex-col gap-4">
        <p className="text-sm text-gray-600">
          Sélectionnez les prestations que cette personne peut réaliser.
        </p>

        <div className="relative shrink-0">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher une prestation…"
            className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm"
          />
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-gray-100 pb-3 text-sm">
          <button
            type="button"
            onClick={selectAll}
            className="font-semibold text-primary-600 hover:underline"
          >
            Tout sélectionner
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={deselectAll}
            className="font-semibold text-gray-600 hover:underline"
          >
            Tout désélectionner
          </button>
          {searchTerm && (
            <span className="ml-auto text-xs text-gray-500">
              {filtered.length} résultat{filtered.length > 1 ? 's' : ''} sur {totalCount}
            </span>
          )}
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto rounded-lg border border-gray-200 p-3 sm:p-4">
          {Object.keys(groups).length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">
              Aucune prestation ne correspond à votre recherche.
            </p>
          ) : (
            Object.entries(groups).map(([category, items]) => {
              const selectedInCat = items.filter((p) => selected.has(p.id)).length;
              const allChecked = selectedInCat === items.length && items.length > 0;
              const someChecked = selectedInCat > 0 && !allChecked;
              return (
                <div key={category} className="rounded-lg border border-gray-100 bg-gray-50/50 p-3">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = someChecked;
                      }}
                      onChange={(e) => toggleCategory(items, e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <span className="flex-1 text-xs font-bold uppercase tracking-wide text-gray-800">
                      {category}
                    </span>
                    <span className="text-xs font-medium text-gray-500">
                      {selectedInCat} / {items.length}
                    </span>
                  </label>
                  <div className="ml-6 mt-2 space-y-0.5">
                    {items.map((p) => (
                      <label
                        key={p.id}
                        className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1.5 text-sm hover:bg-white"
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(p.id)}
                          onChange={() => toggleOne(p.id)}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <span className="text-gray-800">{p.nom}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-center text-sm font-medium text-gray-700 sm:text-left">
            {selectedCount} prestation{selectedCount !== 1 ? 's' : ''} sélectionnée{selectedCount !== 1 ? 's' : ''}
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
